import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormControlLabel, MenuItem, Radio, RadioGroup } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useGetMyCommunities } from '../../../../redux/api/communitiesApi';
import type { IPostsViewPost } from '../../../../redux/api/posts/posts-api.types';
import { useGetMyDetails } from '../../../../redux/api/users/usersApi';
import { ICommunity } from '../../../../types/interfaces/community';
import {
  AppCancelButton,
  AppSaveButton,
} from '../../../designe/buttons/app-button/AppButton';
import AppTextField from '../../../designe/textfields/app-text-field/AppTextField';
import { useStyles } from './ReplyPost.styles';

enum OptionId {
  privateMessage = 'privateMessage',
  communityPost = 'communityPost',
  pagePost = 'pagePost',
}

interface IReplyPostProps {
  onClose: () => void;
  onSavePost: (post: IPostsViewPost) => void;
}

export default function ReplyPost({ onClose, onSavePost }: IReplyPostProps) {
  const [newPostContent, setNewPostContent] = useState();
  const [loader, setLoader] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState<ICommunity>(null);
  const [replayTo, setReplayTo] = useState<OptionId>(OptionId.pagePost);
  const { t, i18n } = useTranslation();
  const [userCommunities, setUserCommunities] = useState([]);
  const { data: user } = useGetMyDetails();
  const { data: communities } = useGetMyCommunities();
  const theme = useTheme();

  const { classes } = useStyles(theme);

  useEffect(() => {
    if (!communities) return;

    const userCommunities = communities.filter((c) => c.ownerId === user.id);
    setSelectedCommunity(userCommunities[0]);
    setUserCommunities(userCommunities);
  }, [communities]);

  function getRadioComponent(option: OptionId, disabled: boolean) {
    return (
      <Radio
        disabled={disabled}
        classes={{ root: classes.optionItem, checked: classes.checked }}
        checked={replayTo === option}
        onChange={() => setReplayTo(option)}
      />
    );
  }

  function onSavePostClick() {
    setLoader(true);
    let promise;

    if (replayTo === OptionId.pagePost) {
      promise = onSavePost({ content: newPostContent, pageUserId: user.id });
    } else if (replayTo === OptionId.communityPost) {
      promise = onSavePost({
        content: newPostContent,
        communityId: selectedCommunity.id,
      });
    }

    promise.finally(() => {
      setLoader(false);
      onClose();
    });
  }

  return (
    <div className={classes.replyPostWrapper}>
      <RadioGroup defaultValue={OptionId.pagePost}>
        <FormControlLabel
          value={OptionId.privateMessage}
          control={getRadioComponent(OptionId.privateMessage, true)}
          label={t('posts.privateMessage')}
        />
        <FormControlLabel
          value={OptionId.communityPost}
          control={getRadioComponent(
            OptionId.communityPost,
            !userCommunities?.length,
          )}
          label={t('posts.communityPost')}
        />
        {replayTo === OptionId.communityPost && (
          <AppTextField
            label={t('common.community')}
            fullWidth
            value={selectedCommunity?.id}
            onChange={(e) =>
              setSelectedCommunity(
                userCommunities.find((uc) => uc.id === e.target.value),
              )
            }
            select>
            {userCommunities.map((community) => (
              <MenuItem
                className={classes.community}
                key={`community-${community.id}`}
                value={community.id}>
                {community.name}
              </MenuItem>
            ))}
          </AppTextField>
        )}

        <FormControlLabel
          value={OptionId.pagePost}
          control={getRadioComponent(OptionId.pagePost, false)}
          label={t('posts.pagePost')}
        />
      </RadioGroup>

      <AppTextField
        placeholder={t('posts.yourMessage')}
        fullWidth
        multiline
        minRows={5}
        maxRows={5}
        value={newPostContent}
        onChange={(e) => setNewPostContent(e.target.value)}
      />
      <div className={classes.replyPostBottom}>
        <AppCancelButton onClick={onClose}>
          {t('common.cancel')}
        </AppCancelButton>
        <AppSaveButton onClick={onSavePostClick} loading={loader}>
          {t('common.save')}
        </AppSaveButton>
      </div>
    </div>
  );
}
